import { useCallback, useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { CheckoutLoginGate, MembershipPlanCard } from "@baluu/ui/components/checkout";
import {
  membershipCheckoutUrl,
  membershipPortalUrl,
  type MembershipPlan,
} from "@baluu/ui/lib/checkout";
import { hydrateSession, useAuthSnapshot } from "~/widgets/lib/auth-client";
import { getSourceInfo } from "~/widgets/lib/getSourceInfo";
import { trackConversion } from "~/widgets/lib/tracking";
import {
  closePurchaseOverlay,
  membershipReturnUrls,
  pickOffers,
  purchaseApiBase,
  rememberMembershipReturn,
} from "~/widgets/lib/purchase-overlay";
import { CustomerAuthDialog } from "../CustomerAuthDialog/CustomerAuthDialog";
import {
  PurchaseOverlayNotice,
  PurchaseOverlayRetry,
  PurchaseOverlayShell,
  PurchaseOverlaySkeleton,
  useSchoolCheckoutConfig,
} from "../PurchaseOverlay";
import { membershipView } from "../PurchaseOverlay/views";
import { useRequest } from "../PurchaseOverlay/useRequest";

export type MembershipCheckoutOverlayProps = {
  businessId: string;
  businessSlug: string;
  apiUrl?: string;
  primaryColor?: string;
  /** One plan to join; none (or one no longer on sale) shows them all. */
  planId?: string;
  /** "success": the buyer is back from Stripe's Checkout — show the
   *  confirmation instead of the plans. */
  result?: string;
};

/**
 * <baluu-membership-checkout-overlay>: join a membership on the page, as the
 * hosted booking site's /memberships page does. The plans (and, for a
 * signed-in customer, which one they already hold) from
 * `GET /widget/pages/:school/memberships`, drawn by @baluu/ui's
 * MembershipPlanCard. A membership is a Stripe subscription: "Subscribe"
 * asks `POST /widget/app/membership/checkout` (the server resolves the
 * school's Connect account) for Stripe's Checkout page and goes there; Stripe
 * brings the buyer back to this page with `?baluu_membership=success`, where
 * the bundle reopens this overlay with `result="success"` as it loads (resumeMembershipReturn). A member manages
 * their plan through `POST /widget/me/membership-portal`. A school that asks
 * customers to sign in before booking asks before a membership too.
 */
export function MembershipCheckoutOverlay({
  businessId,
  businessSlug,
  apiUrl,
  primaryColor = "#03c2b9",
  planId,
  result,
}: MembershipCheckoutOverlayProps) {
  const base = purchaseApiBase(apiUrl);
  const school = useSchoolCheckoutConfig(businessId, apiUrl);
  const auth = useAuthSnapshot();
  const email = auth.status === "authenticated" ? auth.user?.email : undefined;
  const [error, setError] = useState<string | null>(null);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [managing, setManaging] = useState<string | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const succeeded = result === "success";
  const settings = school.status === "ready" ? school.config : null;

  useEffect(() => {
    void hydrateSession(apiUrl, businessSlug);
  }, [apiUrl, businessSlug]);

  // The plans, asked again with the customer's email once they're signed
  // in, so their own plan shows as theirs (the hosted page does the same).
  // Not before the session is known, and each ask reads as loading until
  // its own answer — a customer who just signed in never sees Subscribe on
  // their own plan meanwhile — and a failure recovers on retry.
  const plansUrl =
    succeeded || auth.status === "unknown"
      ? null
      : `${base}/widget/pages/${encodeURIComponent(businessSlug)}/memberships${email ? `?buyerEmail=${encodeURIComponent(email)}` : ""}`;
  const {
    data: plans,
    failed,
    retry: reload,
  } = useRequest<MembershipPlan[]>(plansUrl, async () => {
    const r = await fetch(plansUrl!);
    if (!r.ok) return null;
    const body = await r.json();
    return Array.isArray(body?.data?.plans) ? body.data.plans : [];
  });

  const close = useCallback(() => closePurchaseOverlay("membership"), []);

  const view = membershipView({
    config: school.status,
    requireLogin: Boolean(settings?.requireLoginToBook),
    auth: auth.status,
    plans,
    plansFailed: failed,
  });

  const subscribe = async (plan: MembershipPlan) => {
    // Only from the plans as they are for this customer, with the school's
    // sign-in rule known and met.
    if (view !== "plans") return;
    setError(null);
    setSubscribing(plan.id);
    try {
      const { successUrl, cancelUrl } = membershipReturnUrls(window.location.href);
      // So the bundle can show the confirmation when Stripe sends them back.
      rememberMembershipReturn({ businessId, businessSlug, apiUrl, primaryColor, planId: plan.id });
      const response = await fetch(`${base}/widget/app/membership/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          membershipPlanId: plan.id,
          schoolId: businessId,
          buyerEmail: email,
          successUrl,
          cancelUrl,
          source: "widget",
          sourceInfo: getSourceInfo(),
        }),
      });
      const body = await response.json().catch(() => null);
      const url = response.ok ? membershipCheckoutUrl(body) : null;
      if (!url) throw new Error(body?.message || body?.error || "We couldn't start your membership. Please try again.");
      window.location.assign(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "We couldn't start your membership. Please try again.");
      setSubscribing(null);
    }
  };

  const manage = async (plan: MembershipPlan) => {
    if (!plan.subscriptionId || !auth.token) return;
    setError(null);
    setManaging(plan.id);
    try {
      const response = await fetch(`${base}/widget/me/membership-portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${auth.token}` },
        body: JSON.stringify({ membershipSubscriptionId: plan.subscriptionId, returnUrl: window.location.href }),
      });
      const url = response.ok ? membershipPortalUrl(await response.json().catch(() => null)) : null;
      if (!url) throw new Error("We couldn't open your membership. Please try again.");
      window.location.assign(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "We couldn't open your membership. Please try again.");
      setManaging(null);
    }
  };

  // Back from Stripe: count the conversion once, as the other purchases do.
  useEffect(() => {
    if (!succeeded) return;
    const detail = { businessId, planId };
    document.dispatchEvent(new CustomEvent("baluu:membership-purchased", { bubbles: true, composed: true, detail }));
    trackConversion("baluu_membership_purchased", { businessId });
  }, [succeeded, businessId, planId]);

  const authDialog = (
    <CustomerAuthDialog
      open={authOpen}
      onOpenChange={setAuthOpen}
      apiUrl={apiUrl}
      businessSlug={settings?.slug || businessSlug}
      schoolName={settings?.name}
      primaryColor={primaryColor}
    />
  );

  if (succeeded) {
    return (
      <PurchaseOverlayShell title="You're a member" onClose={close} nestedOpen={authOpen}>
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <CheckCircle2 className="h-12 w-12 text-primary" aria-hidden />
          <p className="text-sm text-muted-foreground">
            Thank you for joining{settings?.name ? ` ${settings.name}` : ""}. Your membership starts as soon as your payment
            is confirmed, and you&apos;ll find it in your profile.
          </p>
          <Button onClick={close}>Done</Button>
        </div>
      </PurchaseOverlayShell>
    );
  }

  const shown = plans ? pickOffers(plans, planId) : [];
  const single = shown.length === 1 && Boolean(planId);

  return (
    <PurchaseOverlayShell
      title={single ? shown[0]!.name : "Memberships"}
      intro={single ? undefined : "Join a membership and get access to classes every month."}
      onClose={close}
      wide={view === "plans" && !single && shown.length > 1}
      nestedOpen={authOpen}
    >
      {view === "config-error" ? (
        <PurchaseOverlayRetry onRetry={school.retry}>We couldn&apos;t load the shop&apos;s settings.</PurchaseOverlayRetry>
      ) : view === "loading" ? (
        <PurchaseOverlaySkeleton />
      ) : view === "gated" ? (
        <CheckoutLoginGate schoolName={settings?.name} onLogin={() => setAuthOpen(true)} className="min-h-0 py-2" />
      ) : view === "plans-error" ? (
        <PurchaseOverlayRetry onRetry={reload}>We couldn&apos;t load the memberships.</PurchaseOverlayRetry>
      ) : view === "empty" || shown.length === 0 ? (
        <PurchaseOverlayNotice>There are no memberships to join right now.</PurchaseOverlayNotice>
      ) : (
        <div className="flex flex-col gap-4">
          {error ? (
            <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <div className={shown.length > 1 ? "grid gap-4 md:grid-cols-2 lg:grid-cols-3" : "grid gap-4"}>
            {shown.map((plan) => (
              <MembershipPlanCard
                key={plan.id}
                plan={plan}
                onSubscribe={subscribe}
                onManage={auth.token ? manage : undefined}
                subscribing={subscribing === plan.id}
                managing={managing === plan.id}
                busy={Boolean(subscribing || managing)}
              />
            ))}
          </div>
          {auth.status !== "authenticated" ? (
            <p className="text-center text-sm text-muted-foreground">
              Already a member?{" "}
              <button type="button" className="font-medium text-primary underline-offset-4 hover:underline" onClick={() => setAuthOpen(true)}>
                Sign in
              </button>{" "}
              to manage your membership.
            </p>
          ) : null}
        </div>
      )}
      {authDialog}
    </PurchaseOverlayShell>
  );
}
